import type { DatasetAttributes, DatasetPayload } from 'common'
import { coreClient } from '@/api/coreclient'
import { CancelablePromise } from 'common'
import { CancelablePromiseWrapper } from '@/utils'

export default {
  async create(
    body: Omit<
      DatasetAttributes,
      | 'id'
      | 'created_on'
      | 'modified_on'
      | 'deleted_on'
      | 'user_id'
      | 'next_execution'
      | 'last_execution'
    >
  ) {
    const client = await coreClient()
    return client.dataset
      .create(body)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async list() {
    const client = await coreClient()
    return client.dataset
      .list()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as DatasetAttributes[]
      })
  },
  async get(id: number, options?: { logVisit?: boolean }) {
    const client = await coreClient()
    return client.dataset
      .get(id, options?.logVisit)
      .then((response) => response.data)
      .catch((e) => {
        console.error(e)
        throw e
      })
  },
  async update(
    id: number,
    body: Omit<
      DatasetAttributes,
      | 'id'
      | 'created_on'
      | 'modified_on'
      | 'deleted_on'
      | 'next_execution'
      | 'user_id'
      | 'last_execution'
    >
  ) {
    const client = await coreClient()
    return client.dataset
      .update(id, body)
      .then((response) => {
        if (response.data) {
          return response.data
        }
        return null
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async deleteDataset(id: number) {
    const client = await coreClient()
    return client.dataset
      .deleteDataset(id)
      .then((response) => {
        return response
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async sendEmail(payload: DatasetAttributes, format: 'csv' | 'jsonl') {
    const client = await coreClient()
    return client.dataset
      .sendEmail({
        dataset: payload.query,
        emails: payload.emails,
        name: payload.name,
        format
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  },

  async exportDataset(
    payload: DatasetPayload,
    name: string,
    emails: string[],
    format: 'csv' | 'jsonl'
  ) {
    const client = await coreClient()
    return client.dataset.sendEmail({ dataset: payload, emails, name, format })
  },

  previewDataset(
    payload: DatasetPayload
  ): CancelablePromiseWrapper<(string | number | boolean | null)[][]> {
    return new CancelablePromiseWrapper(
      new CancelablePromise((resolve, reject, onCancel) => {
        coreClient()
          .then((client) => {
            const request = client.dataset.previewDataset(payload)

            // Handle cancellation by forwarding it to the underlying request
            onCancel(() => {
              request.cancel()
              this.cancelDataset().catch(console.error)
            })

            request
              .then((response) => {
                resolve(response)
              })
              .catch(reject)
          })
          .catch(reject)
      })
    )
  },

  async cancelDataset() {
    const client = await coreClient()
    client.dataset.cancelDataset()
  },

  async generateReport(query: string) {
    const client = await coreClient()
    return client.dataset.generateReport({ query })
  },

  async syncDatabases() {
    const client = await coreClient()
    return client.dataset.syncDatabases()
  },

  async estimateComplexity(payload: DatasetPayload) {
    const client = await coreClient()
    return client.dataset
      .estimateComplexity(payload)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        throw e
      })
  }
}
